import React, { useState, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from '../../redux/store';
import { getProperty } from "../../fsConfig";

import { fetchTravelerProfile } from '../../redux/entities/travelers/travelerProfileActions';
import { changeTravelerPassword } from '../../service/travelerProfileService';
import { selectTravelerProfile } from '../../redux/entities/travelers/travelerProfileSelectors';
import { passwordValidation, hasValidationErrors } from '../../lib/airframe/FormValidation';
import { selectImageUrl } from '../../redux/entities/brandingImage/brandingImageSelectors';
import { TravelerProfileFormHandler } from './TravelerProfileFormHandler';

import { CiriumWrapperWithHeader } from '../../components/cirium/CiriumWrapperWithHeader';
import { CiriumPage } from '../../components/cirium/CiriumPage';
import { CiriumErrorPage } from '../../components/errors/CiriumErrorPage';
import { TravelerProfileForm } from './TravelerProfileForm';

import { ToastConsumer } from '@flightstats/airframe-react';
import { goTo, hasWorldAware } from '../../configTdm';
import { TravelerProfile, TravelerLogout} from '../../appRoutes';

interface State {
  showError: boolean;
  loading: boolean;
  changePasswordFormDisabled: boolean;
  passwordChanged: boolean;
  hasFormError: boolean;
}

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ErrorResponse {
  message: string;
}

const TravelerProfileContainer: React.FC = () => {
  const dispatch = useDispatch();
  const initialTravelerProfile = useSelector(selectTravelerProfile);
  const brandingImageUrl = useSelector(selectImageUrl);
  const { accountId, supportedSMSCountryCodes } = getProperty('session', {});

  const [state, setState] = useState<State>({
    showError: false,
    loading: false,
    changePasswordFormDisabled: false,
    passwordChanged: false,
    hasFormError: false,
  });

  useEffect(() => {
    setState(prevState => ({ ...prevState, loading: true }));
    dispatch(fetchTravelerProfile(hasWorldAware()))
      .then((response: any) => {
        setState(prevState => ({ ...prevState, loading: false, showError: !!response.error }));
      });
  }, [dispatch]);

  const handleChangePasswordError = (response: ErrorResponse, errors: any, addToast: (message: string, options: { variant: string }) => void) => {
    if (response.message.includes('Invalid user name or password')) {
      errors.updateFieldErrorText('currentPassword', 'Invalid Password');
    } else {
      addToast('Change password is temporarily unavailable', { variant: 'error' });
    }
  };

  const changePasswordInfo = (values: ChangePasswordValues) => ({
    currentPassword: values.currentPassword,
    newPassword: values.newPassword,
    confirmPassword: values.confirmPassword,
  });

  const handleChangePasswordSubmit = (errors: any, values: ChangePasswordValues, addToast: (message: string, options: { variant: string }) => void) => {
    if (!hasValidationErrors(errors)) {
      setState(prevState => ({ ...prevState, changePasswordFormDisabled: true, loading: true }));
      changeTravelerPassword(changePasswordInfo(values))
        .then(() => {
          setState(prevState => ({ ...prevState, passwordChanged: true }));
        })
        .catch(error => {
          handleChangePasswordError(error, errors, addToast);
        })
        .finally(() => {
          setState(prevState => ({ ...prevState, changePasswordFormDisabled: false, loading: false }));
        });
    }
  };

  const content = state.showError ? (
    <CiriumErrorPage />
  ) : (
    <ToastConsumer>
      {({ addToast }) => (
        <CiriumPage title="Traveler Profile">
          {initialTravelerProfile && initialTravelerProfile.travelerKey && (
            <TravelerProfileFormHandler
              accountId={accountId}
              isAgent={false}
              loading={state.loading}
              setLoading={loading => setState(prevState => ({ ...prevState, loading }))}
              travelerKey={initialTravelerProfile.travelerKey}
              travelerProfile={initialTravelerProfile}
              addToast={addToast}
              render={(
                handleTestAlert,
                testAlertSent,
                testAlertSentContact,
                handleOnCloseTestAlertSent,
                handleSubmit,
                loading,
                hasFormError,
                testAlertError,
                saveProfilePhoneError,
                setSaveProfilePhoneError,
              ) => (
                <TravelerProfileForm
                  saveProfilePhoneError={saveProfilePhoneError}
                  setSaveProfilePhoneError={setSaveProfilePhoneError}
                  email={initialTravelerProfile.email}
                  disabled={loading}
                  hasFormError={hasFormError}
                  passwordValidation={passwordValidation}
                  changePasswordDisabled={state.changePasswordFormDisabled}
                  passwordChanged={state.passwordChanged}
                  onSubmit={handleSubmit}
                  onTestAlert={handleTestAlert}
                  testAlertSent={testAlertSent}
                  onCloseTestAlertSent={handleOnCloseTestAlertSent}
                  testAlertSentContact={testAlertSentContact}
                  onChangePasswordSubmit={handleChangePasswordSubmit}
                  passwordChangedConfirmation={() => goTo(TravelerProfile)}
                  supportedSMSCountryCodes={supportedSMSCountryCodes}
                  testAlertError={testAlertError}
                />
              )}
            />
          )}
        </CiriumPage>
      )}
    </ToastConsumer>
  );

  return (
    <CiriumWrapperWithHeader
      logout={() => {
        // Clear cache before going to logout
        try {
          sessionStorage.removeItem('mapFilters');
        } catch (error) {
          console.error('Error clearing cache during traveler logout:', error);
        }
        goTo(TravelerLogout);
      }}
      loading={state.loading}
      appName="Trip Alert"
      brandingImageUrl={brandingImageUrl || undefined}
    >
      {content}
    </CiriumWrapperWithHeader>
  );
};

const WrappedTravelerProfileContainer: React.FC = () => (
  <Provider store={store}>
    <TravelerProfileContainer />
  </Provider>
);

export default WrappedTravelerProfileContainer;